import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import DevelopersIcon from '@interxfi/design-system/icons/react/icon-developers';
import type { IxmUserauthV1ProgramControlsApiAuth, IxmUserauthV1ProgramControlsApiAuthLimits } from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../util/has-value';
import { getNumericStringValidator } from '../../../../../../util/validation';

export const programControlsApiAuthFormSchema = z.object({
  apiKeys: z.object({
    canCreate: z.object({
      value: z.boolean(),
      apiKeysMax: getNumericStringValidator('Max API Keys', { required: '', maxDecimalPlaces: 0, gte: 0 }),
    }),
    canRevoke: z.boolean(),
    canGetToken: z.boolean(),
  }),
});

export type ProgramControlsApiAuthFormValues = z.infer<typeof programControlsApiAuthFormSchema>;

export const programControlsApiAuthFormDefaultValues: ProgramControlsApiAuthFormValues = {
  apiKeys: {
    canCreate: {
      value: false,
      apiKeysMax: '',
    },
    canRevoke: false,
    canGetToken: false,
  },
};

export function getInitialFormValuesForProgramApiAuthControls(
  data: IxmUserauthV1ProgramControlsApiAuth | undefined,
): ProgramControlsApiAuthFormValues {
  if (!data) {
    return programControlsApiAuthFormDefaultValues;
  }

  return {
    apiKeys: {
      canCreate: {
        value: Boolean(data.settings?.canCreateApiKeys),
        apiKeysMax: data.limits?.apiKeysMax?.toString() || '',
      },
      canRevoke: Boolean(data.settings?.canRevokeApiKeys),
      canGetToken: Boolean(data.settings?.canGetToken),
    },
  };
}

export function formValuesToProgramApiAuthControls(values: ProgramControlsApiAuthFormValues): IxmUserauthV1ProgramControlsApiAuth {
  return {
    settings: {
      canCreateApiKeys: values.apiKeys.canCreate.value,
      canRevokeApiKeys: values.apiKeys.canRevoke,
      canGetToken: values.apiKeys.canGetToken,
    },
    limits: addDefinedValues<IxmUserauthV1ProgramControlsApiAuthLimits>(
      {},
      {
        apiKeysMax: values.apiKeys.canCreate.apiKeysMax ? Number(parseNumber(values.apiKeys.canCreate.apiKeysMax)) : undefined,
      },
    ),
  };
}

interface ProgramApiAuthControlsFormProps {
  lens: Lens<ProgramControlsApiAuthFormValues>;
}

export function ProgramApiAuthControlsForm({ lens }: ProgramApiAuthControlsFormProps) {
  const { setValue } = useFormContext();
  const canCreateApiKeys = useWatch(lens.focus('apiKeys.canCreate.value').interop());
  const apiKeysMaxLens = lens.focus('apiKeys.canCreate.apiKeysMax');
  const { name: apiKeysMaxName } = apiKeysMaxLens.interop();
  const handleToggleCreateApiKeys = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) {
        setValue(apiKeysMaxName, '', { shouldValidate: true });
      }
    },
    [apiKeysMaxName, setValue],
  );

  return (
    <AccordionFormGroup icon={<DevelopersIcon aria-hidden />} title="Developers">
      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens
            align="space-between"
            label="Create API Keys"
            lens={lens.focus('apiKeys.canCreate.value')}
            onChange={handleToggleCreateApiKeys}
          />
        }
      >
        {canCreateApiKeys ? <AppTextInputWithLens label="API Key Max" lens={apiKeysMaxLens} /> : null}
      </AccordionFormGroup.Section>

      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Revoke API Keys" lens={lens.focus('apiKeys.canRevoke')} />}
      />

      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Get API Token" lens={lens.focus('apiKeys.canGetToken')} />}
      />
    </AccordionFormGroup>
  );
}
